import type { SimEvent } from '@fafi/shared';

// Client HUD event ticker (§13.1). Consumes MatchEvent (0x85) messages keyed by (tick, idxWithinTick):
// processes each key exactly once (reconnect re-sends are idempotent, §4.4) and keeps the display list in
// (tick, idx) order regardless of arrival order. Pure presentation — reads events, never sim state.

export interface TickerEntry {
  tick: number;
  idx: number;
  event: SimEvent;
}

/** Notable events surfaced in the ticker feed (goals, cards, whistles, …). */
const TICKER_KINDS = new Set<SimEvent['t']>(['GOAL', 'CARD', 'MISS', 'SHOT', 'FOUL', 'SAVE', 'DIVE_JUDGED', 'PUNCH_LANDED']);

export class EventTicker {
  private readonly seen = new Set<string>();
  private list: TickerEntry[] = [];

  constructor(private readonly cap = 8) {}

  /** Ingest a (tick, idx, event). Returns false if this (tick, idx) was already seen (dedup). */
  ingest(tick: number, idx: number, event: SimEvent): boolean {
    const key = `${tick}:${idx}`;
    if (this.seen.has(key)) return false;
    this.seen.add(key);
    if (!TICKER_KINDS.has(event.t)) return true; // counted (exactly-once) but not surfaced in the feed
    this.list.push({ tick, idx, event });
    this.list.sort((a, b) => a.tick - b.tick || a.idx - b.idx); // stable (tick, idx) order
    while (this.list.length > this.cap) this.list.shift();
    return true;
  }

  /** D46 Item 6: forget everything for a NEW match epoch. A rematch resets the sim to tick 0, so the old
   *  (tick, idx) keys would collide and drop every event of the new match (no PHASE ⇒ frozen end screen). */
  reset(): void {
    this.seen.clear();
    this.list = [];
  }

  /** The ordered ticker entries (oldest → newest). */
  entries(): readonly TickerEntry[] {
    return this.list;
  }
  /** The last `n` entries (newest slice), oldest → newest. */
  recent(n: number): readonly TickerEntry[] {
    return this.list.slice(Math.max(0, this.list.length - n));
  }
}
